import type { SxProps, Theme } from '@mui/material'

// ─── ActionFilterPopover styles ─────────────────────────────────────

export const actionFilterPopoverPaperSx: SxProps<Theme> = {
  mt: 0.5,
  maxHeight: 400,
  width: 280,
  borderRadius: 2,
  boxShadow: (theme) => theme.shadows[8],
  display: 'flex',
  flexDirection: 'column',
}

export const actionFilterSearchBoxSx: SxProps<Theme> = {
  px: 1.5,
  pt: 1.5,
  pb: 1,
  flexShrink: 0,
}

export const actionFilterSearchIconSx: SxProps<Theme> = {
  fontSize: '1rem',
  color: 'text.disabled',
}

export const actionFilterClearButtonSx: SxProps<Theme> = {
  p: 0.25,
}

export const actionFilterClearIconSx: SxProps<Theme> = {
  fontSize: '0.875rem',
}

export const actionFilterSearchInputSx: SxProps<Theme> = {
  fontSize: '0.8125rem',
  borderRadius: 1.5,
  height: 32,
  bgcolor: (theme) => theme.palette.grey[50],
  '& fieldset': { border: 'none' },
  '&.Mui-focused': {
    bgcolor: (theme) => theme.palette.background.paper,
    '& fieldset': { border: '1px solid', borderColor: 'primary.main' },
  },
}

export const actionFilterMenuListSx: SxProps<Theme> = {
  py: 0,
  flex: 1,
  overflow: 'auto',
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': { width: 4 },
  '&::-webkit-scrollbar-thumb': { bgcolor: (theme) => theme.palette.grey[300], borderRadius: 2 },
}

export const actionFilterGroupMenuItemSx: SxProps<Theme> = {
  py: 0.25,
  px: 1.5,
  minHeight: 26,
  bgcolor: (theme) => theme.palette.grey[50],
}

export const actionFilterGroupCheckboxSx: SxProps<Theme> = {
  p: 0,
  mr: 1,
}

export const actionFilterActionMenuItemSx: SxProps<Theme> = {
  py: 0.125,
  px: 1.5,
  pl: 3.5,
  minHeight: 28,
}

export const actionFilterActionCheckboxSx: SxProps<Theme> = {
  p: 0,
  mr: 1,
}

export const actionFilterNoResultsSx: SxProps<Theme> = {
  py: 2,
  px: 1.5,
  textAlign: 'center',
}

// ─── ActivityTab styles ─────────────────────────────────────────────

export const activityTabRootSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflow: 'hidden',
}

export const searchBarContainerSx = (fullWidth?: boolean): SxProps<Theme> => ({
  pt: fullWidth ? 2 : 1.5,
  pb: 1,
  px: fullWidth ? 0 : 1.5,
})

export const searchBarIconSx: SxProps<Theme> = {
  fontSize: 20,
  color: 'text.secondary',
}

export const searchBarClearIconSx: SxProps<Theme> = {
  fontSize: '1rem',
}

export const searchBarTextFieldSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.5,
    backgroundColor: 'background.paper',
    fontSize: '0.875rem',
  },
}

export const filterChipsRowSx = (fullWidth?: boolean): SxProps<Theme> => ({
  px: fullWidth ? 0 : 1.5,
  pb: 1,
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  flexWrap: 'nowrap',
  overflowX: 'auto',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
})

export const filterChipIconSx: SxProps<Theme> = {
  fontSize: '0.875rem !important',
}

export const resultsCountSx: SxProps<Theme> = {
  ml: 0.5,
  whiteSpace: 'nowrap',
}

export const spacerSx: SxProps<Theme> = {
  flex: 1,
}

export const refreshButtonSx: SxProps<Theme> = {
  p: 0.5,
}

export const refreshIconSx: SxProps<Theme> = {
  fontSize: '1rem',
  color: 'text.secondary',
}

export const eventsListContainerSx = (fullWidth?: boolean): SxProps<Theme> => ({
  flex: 1,
  overflow: 'auto',
  px: fullWidth ? 0 : 1.5,
  pb: 1,
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': { width: 4 },
  '&::-webkit-scrollbar-thumb': { bgcolor: (theme) => theme.palette.grey[300], borderRadius: 2 },
})

export const loadingSkeletonContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
}

export const loadingSkeletonItemSx: SxProps<Theme> = {
  borderRadius: 1.5,
}

export const emptyStateSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  py: 6,
  gap: 1,
}

export const emptyStateTitleSx: SxProps<Theme> = {
  fontWeight: 500,
}

export const emptyStateIconSx: SxProps<Theme> = {
  fontSize: '2rem',
  color: 'text.disabled',
}

export const emptyStateSubtitleSx: SxProps<Theme> = {
  textAlign: 'center',
  px: 2,
}

export const eventListColumnSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
}

export const loadingMoreSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  py: 1.5,
}

export const footerSx = (fullWidth?: boolean): SxProps<Theme> => ({
  px: fullWidth ? 0 : 1.5,
  py: 0.75,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderTop: '1px solid',
  borderColor: 'grey.100',
  bgcolor: (theme) => theme.palette.grey[50],
})

// ─── Date Range Popover styles ──────────────────────────────────────

export const datePopoverPaperSx: SxProps<Theme> = {
  mt: 0.5,
  width: 200,
  borderRadius: 2,
  boxShadow: (theme) => theme.shadows[8],
}

export const datePopoverHeaderSx: SxProps<Theme> = {
  px: 2,
  pt: 1.5,
  pb: 0.5,
}

export const datePopoverMenuListSx: SxProps<Theme> = {
  py: 0.5,
}

export const datePopoverMenuItemSx: SxProps<Theme> = {
  py: 0.75,
  px: 2,
  minHeight: 32,
}

// ─── ActivityEventRow styles ────────────────────────────────────────

export const eventRowRootSx = (isExpanded: boolean): SxProps<Theme> => ({
  borderBottom: '1px solid',
  borderColor: isExpanded ? 'grey.200' : 'grey.50',
  '&:last-child': { borderBottom: 'none' },
})

export const eventRowHeaderSx = (isExpanded: boolean, fullWidth: boolean): SxProps<Theme> => ({
  cursor: 'pointer',
  transition: 'background 0.1s ease',
  bgcolor: isExpanded ? (theme) => theme.palette.grey[100] : 'transparent',
  '&:hover': { bgcolor: (theme) => theme.palette.grey[100] },
  userSelect: 'none',
  px: fullWidth ? 0 : 1,
  py: 0.5,
})

export const eventRowFlexSx: SxProps<Theme> = {
  display: 'flex',
  gap: 0.75,
}

export const eventRowLeftColumnSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 0.75,
  pt: 0.125,
  flexShrink: 0,
}

export const expandIconSx = (isExpanded: boolean): SxProps<Theme> => ({
  fontSize: '1rem',
  color: 'text.disabled',
  transition: 'transform 0.2s ease',
  transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
})

export const eventAvatarSx = (bgColor: string): SxProps<Theme> => ({
  width: 20,
  height: 20,
  fontSize: '0.5rem',
  fontWeight: 600,
  bgcolor: bgColor,
  cursor: 'pointer',
})

export const eventAvatarNonClickableSx = (bgColor: string): SxProps<Theme> => ({
  width: 20,
  height: 20,
  fontSize: '0.5rem',
  fontWeight: 600,
  bgcolor: bgColor,
  cursor: 'default',
})

export const eventRowRightColumnSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
}

export const actionRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
}

export const actionLabelSx: SxProps<Theme> = {
  fontWeight: 500,
  color: 'text.primary',
  fontSize: '0.75rem',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  flex: 1,
  minWidth: 0,
}

export const timeAgoSx: SxProps<Theme> = {
  whiteSpace: 'nowrap',
  flexShrink: 0,
  fontSize: '0.625rem',
}

export const resourceChipSx: SxProps<Theme> = {
  height: 16,
  '& .MuiChip-label': {
    px: 0.5,
    fontSize: '0.5625rem',
    fontWeight: 500,
    lineHeight: 1,
  },
  borderRadius: 0.75,
  flexShrink: 0,
}

export const summaryHintSx: SxProps<Theme> = {
  display: 'block',
  fontSize: '0.6875rem',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  lineHeight: 1.3,
  mt: 0.25,
}

// ─── Expanded Detail Panel styles ───────────────────────────────────

export const expandedPanelSx = (fullWidth: boolean): SxProps<Theme> => ({
  px: fullWidth ? 0 : 1.5,
  py: 1.25,
  bgcolor: (theme) => theme.palette.grey[50],
  borderTop: '1px solid',
  borderColor: 'grey.100',
})

export const expandedSummarySx: SxProps<Theme> = {
  fontSize: '0.8125rem',
  color: 'text.primary',
  mb: 1.25,
  lineHeight: 1.5,
  whiteSpace: 'pre-line',
}

export const contextChipsContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 0.5,
  mb: 1,
}

export const resourceIdChipSx: SxProps<Theme> = {
  height: 22,
  '& .MuiChip-label': {
    px: 0.75,
    fontSize: '0.6875rem',
    fontWeight: 500,
    fontFamily: 'monospace',
  },
  borderRadius: 1,
  borderColor: 'grey.300',
}

export const contextItemChipSx: SxProps<Theme> = {
  height: 22,
  '& .MuiChip-label': { px: 0.75, fontSize: '0.6875rem', fontWeight: 500 },
  borderRadius: 1,
  borderColor: 'grey.300',
}

export const propertyRowsContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
}

export const timestampTextSx: SxProps<Theme> = {
  fontSize: '0.75rem',
  color: 'text.primary',
}

export const tzLabelSx: SxProps<Theme> = {
  fontSize: '0.625rem',
  ml: 0.5,
}

export const timeAgoDetailSx: SxProps<Theme> = {
  fontSize: '0.625rem',
  ml: 0.5,
}

export const actionedByContainerSx = (hasUserId: boolean): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
  cursor: hasUserId ? 'pointer' : 'default',
  '&:hover .prop-user': hasUserId ? { color: 'primary.main' } : {},
})

export const detailAvatarSx = (bgColor: string): SxProps<Theme> => ({
  width: 18,
  height: 18,
  fontSize: '0.5rem',
  fontWeight: 600,
  bgcolor: bgColor,
})

export const detailUserNameSx: SxProps<Theme> = {
  fontSize: '0.75rem',
  fontWeight: 500,
}

export const affectedUserContainerSx = (hasUserId: boolean): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
  cursor: hasUserId ? 'pointer' : 'default',
  '&:hover .prop-affected-user': hasUserId ? { color: 'primary.main' } : {},
})

export const tagsContainerSx: SxProps<Theme> = {
  display: 'flex',
  gap: 0.5,
  flexWrap: 'wrap',
}

export const tagChipSx: SxProps<Theme> = {
  height: 18,
  '& .MuiChip-label': { px: 0.5, fontSize: '0.625rem' },
}

export const ipAddressTextSx: SxProps<Theme> = {
  fontSize: '0.6875rem',
  fontFamily: 'monospace',
  color: 'text.secondary',
}

// ─── Changes Section styles ─────────────────────────────────────────

export const changesSectionSx: SxProps<Theme> = {
  mt: 1,
  pt: 1,
  borderTop: '1px solid',
  borderColor: 'grey.100',
}

export const changesHeaderSx: SxProps<Theme> = {
  fontWeight: 600,
  fontSize: '0.625rem',
  textTransform: 'uppercase',
  letterSpacing: 0.3,
  color: 'text.disabled',
  display: 'block',
  mb: 0.75,
}

export const jsonDiffContainerSx: SxProps<Theme> = {
  mb: 1,
}

export const jsonDiffLabelSx: SxProps<Theme> = {
  fontWeight: 500,
  color: 'text.secondary',
  display: 'block',
  mb: 0.25,
  fontSize: '0.6875rem',
}

export const changeRowSx: SxProps<Theme> = {
  mb: 0.75,
  p: 0.75,
  borderRadius: 1,
  bgcolor: (theme) => theme.palette.background.paper,
  border: '1px solid',
  borderColor: 'grey.100',
}

export const changeFieldLabelSx: SxProps<Theme> = {
  fontWeight: 500,
  color: 'text.secondary',
  display: 'block',
  mb: 0.25,
  fontSize: '0.625rem',
  textTransform: 'capitalize',
}

export const changeValueContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
}

export const oldValueSx: SxProps<Theme> = {
  fontSize: '0.6875rem',
  color: 'error.main',
  wordBreak: 'break-word',
  flex: 1,
  minWidth: 0,
}

export const changeArrowSx: SxProps<Theme> = {
  fontSize: '0.75rem',
  flexShrink: 0,
}

export const newValueSx: SxProps<Theme> = {
  fontSize: '0.6875rem',
  color: 'success.main',
  wordBreak: 'break-word',
  flex: 1,
  minWidth: 0,
}

// ─── PropertyRow styles ─────────────────────────────────────────────

export const propertyRowRootSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1,
  py: 0.375,
  borderBottom: '1px solid',
  borderColor: 'grey.50',
  '&:last-child': { borderBottom: 'none' },
}

export const propertyRowLabelSx: SxProps<Theme> = {
  width: 80,
  flexShrink: 0,
  fontSize: '0.625rem',
  fontWeight: 600,
  color: 'text.disabled',
  textTransform: 'uppercase',
  letterSpacing: 0.2,
  pt: 0.125,
}

export const propertyRowValueContainerSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
}

export const propertyRowValueFlexSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 0.5,
}

export const propertyRowValueTextSx: SxProps<Theme> = {
  fontSize: '0.75rem',
  color: 'text.primary',
  wordBreak: 'break-word',
}

export const propertyRowSubValueSx: SxProps<Theme> = {
  fontSize: '0.625rem',
}

// ─── Shared chip styles ─────────────────────────────────────────────

export const chipSx: SxProps<Theme> = {
  height: 28,
  borderRadius: 1.5,
  '& .MuiChip-label': { fontSize: '0.75rem', px: 1 },
  '& .MuiChip-icon': { ml: 0.5 },
  '& .MuiChip-deleteIcon': { fontSize: '0.875rem' },
}

export const activeChipSx: SxProps<Theme> = {
  height: 28,
  borderRadius: 1.5,
  '& .MuiChip-label': { fontSize: '0.75rem', px: 1 },
  '& .MuiChip-icon': { ml: 0.5, color: 'primary.main' },
  '& .MuiChip-deleteIcon': { fontSize: '0.875rem', color: 'primary.main' },
  bgcolor: 'primary.lightest',
  color: 'primary.main',
  borderColor: 'transparent',
  '&:hover': { bgcolor: 'primary.lightest' },
}
